import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Dialog } from '@headlessui/react';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { GET_USER_ROLE } from 'gql/queries';
import { Modal } from 'components/Modal';
import { InstallationPreviewPanel } from 'components/InstallationPreferences/InstallationPreviewPanel';
import AssistedInstallationRequest from 'components/InstallationPreferences/AssistedInstallationRequest';
import { installationPreviewDomain } from 'utils/installationPreview';

function ScopedInstallationPreview({ storeId, domain, userId, globalAdmin, disconnected }: {
  storeId: string;
  domain: string;
  userId: string;
  globalAdmin: boolean;
  disconnected: boolean;
}) {
  const [requestChanges, setRequestChanges] = useState(false);
  const { data, loading, error } = useQuery<{ storeRoles?: Array<{ role: string }> }>(GET_USER_ROLE, {
    variables: { storeId, userId }, fetchPolicy: 'no-cache', context: { queryDeduplication: false }, skip: globalAdmin,
  });
  const storeRoles = data?.storeRoles;
  const canManage = !disconnected && (globalAdmin || (!loading && !error && Array.isArray(storeRoles)
    && storeRoles.some(row => ['ADMIN', 'MANAGER'].includes(row.role))));
  return <div className="mb-6">
    <InstallationPreviewPanel storeId={storeId} domain={domain} canManage={canManage}
      onRequestChanges={canManage ? () => setRequestChanges(true) : undefined} />
    {error && !globalAdmin && <p role="alert" className="mt-3 text-sm text-amber-800">We could not confirm your editing access. You can still view the latest preview.</p>}
    {disconnected && <p className="mt-3 text-sm text-gray-500">Reconnect your Shopify store before requesting a new preview or installation changes.</p>}
    {requestChanges && canManage && <Modal open openModal={setRequestChanges} containerClass="w-full max-w-2xl">
      <Dialog.Title className="text-lg font-semibold text-gray-900">Request installation changes</Dialog.Title>
      <p className="mt-2 text-sm text-gray-600">Ask our expert team to review your current installation and arrange any changes you need.</p>
      <AssistedInstallationRequest storeId={storeId} domain={domain} canManage={canManage} blocked={false} />
    </Modal>}
  </div>;
}

export default function StoreInstallationPreview() {
  const { storeId, storeProperties, storeLoading } = useStore();
  const { user } = useUser();
  const domain = installationPreviewDomain(storeProperties?.store);
  if (!storeId || storeLoading || !storeProperties || storeProperties.id !== storeId || !domain || !user?.id) {
    return <p role="status" className="mb-6 text-sm text-gray-600">Select a store and wait for its installation preview to load.</p>;
  }
  const globalAdmin = user.defaultRole === 'shipaid_admin';
  return <ScopedInstallationPreview key={`${user.id}:${storeId}:${domain}:${globalAdmin}`}
    storeId={storeId} domain={domain} userId={user.id} globalAdmin={globalAdmin} disconnected={!!storeProperties.uninstalledAt} />;
}
